import * as React from 'react';
import styled from 'styled-components';

import { Modal } from '@process-advisor/process-miner/components/modal/modal';
import { useApiListener } from '@process-advisor/process-miner/shared/hooks/useApiListener';
import { Event } from '@process-advisor/process-miner/api/events';
import {
  getConsultingWorkspace,
  setConsultingWorkspace,
  getModelID,
  getProcessModelTitle,
  getAppVersion,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { type FindingModalProps } from '@process-advisor/process-miner/api/methods/modals/findingModal';

const CATEGORIES = ['Performance', 'Compliance', 'Automation', 'Cost', 'Experience', 'Data Quality', 'Governance'];
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const CONFIDENCE = ['Weak', 'Moderate', 'Strong'];
const VALUES = ['Low', 'Medium', 'High'];
const STATUSES = ['Needs Review', 'Validated', 'Parked', 'Rejected'];

const Shell = styled.div`
  display: grid;
  gap: 10px;
  color: #172b4d;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Guidance = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  border: 1px solid #b3d4ff;
  border-radius: 8px;
  background: #f0f7ff;
  padding: 9px 11px;
  color: #172b4d;

  .icon {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    background: #0c66e4;
    color: #fff;
    font-size: 14px;
    font-weight: 800;
  }

  .copy {
    font-size: 12px;
    line-height: 1.35;
  }

  strong {
    display: block;
    font-size: 12.5px;
    margin-bottom: 2px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 9px 10px;
`;

const Field = styled.label<{ span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: span ${({ span }) => span || 6};

  @media (max-width: 760px) {
    grid-column: span 12;
  }

  span {
    font-size: 12px;
    font-weight: 650;
    color: #344563;
    letter-spacing: 0;
    text-transform: none;
  }

  input, textarea, select {
    border: 1px solid #b8c4d5;
    border-radius: 7px;
    background: #fff;
    color: #172b4d;
    font: inherit;
    font-size: 13px;
    padding: 7px 9px;
    min-height: 34px;
    box-sizing: border-box;
  }

  textarea {
    resize: vertical;
    min-height: ${({ span }) => (span === 12 ? '52px' : '44px')};
    line-height: 1.35;
  }

  input:focus, textarea:focus, select:focus {
    outline: none;
    border-color: #0c66e4;
    box-shadow: 0 0 0 2px #deebff;
  }
`;


const FocusBox = styled.div`
  display: grid;
  gap: 6px;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  background: #fafbfc;
  padding: 8px 10px;
  color: #42526e;
  font-size: 12px;

  .focus-title {
    font-weight: 700;
    color: #172b4d;
  }

  .focus-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const InlineMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 8px 0 0;
  border-top: 1px solid #ebecf0;
  color: #42526e;
  font-size: 11.5px;
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #dfe1e6;
  background: #fff;
  border-radius: 999px;
  padding: 4px 8px;

  strong {
    color: #172b4d;
    font-weight: 700;
  }
`;

const NextStep = styled.div`
  border-left: 3px solid #0c66e4;
  background: #ffffff;
  color: #42526e;
  padding: 7px 10px;
  font-size: 12px;
  line-height: 1.35;

  strong {
    color: #172b4d;
  }
`;

const normaliseWorkspace = (workspace: any) => {
  const parsed = workspace && typeof workspace === 'object' ? workspace : {};
  return {
    version: 4,
    objective: '',
    scope: '',
    businessArea: '',
    processOwner: '',
    stakeholders: '',
    targetSla: '',
    painPoints: '',
    workingHypothesis: '',
    notes: '',
    deliverables: {},
    discovery_rules: [],
    actions: [],
    findings: [],
    ...parsed,
    version: 4,
    findings: Array.isArray(parsed.findings) ? parsed.findings : [],
    actions: Array.isArray(parsed.actions) ? parsed.actions : [],
    discovery_rules: Array.isArray(parsed.discovery_rules) ? parsed.discovery_rules : [],
  };
};

const splitEvidence = (value: any) => Array.isArray(value)
  ? value.map(v => String(v || '').trim()).filter(Boolean)
  : String(value || '').split('\n').map(v => v.trim()).filter(Boolean);


let forgeModule: any = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const saveWorkspaceToBackendBestEffort = async (modelId: any, workspace: any) => {
  if (!modelId) return;
  try {
    const appVersion = getAppVersion ? getAppVersion() : '';
    if (appVersion === 'FORGE') {
      const { invoke } = await loadForgeModule();
      await invoke('saveConsultingWorkspace', { modelId, consulting_workspace: JSON.stringify(workspace) });
    }
  } catch (error) {
    console.warn('[FindingModal] Finding saved locally; backend workspace save failed.', error);
  }
};

const storageKey = (modelId: any) => `metricus.consultingWorkspace.${modelId || 'unsaved'}`;

const loadWorkspace = () => {
  const modelId = getModelID ? getModelID() : '';
  try {
    const raw = window.localStorage.getItem(storageKey(modelId));
    if (raw) return normaliseWorkspace(JSON.parse(raw));
  } catch (_) {}
  return normaliseWorkspace(getConsultingWorkspace ? getConsultingWorkspace() : null);
};

const saveWorkspace = (workspace: any) => {
  const modelId = getModelID ? getModelID() : '';
  const next = normaliseWorkspace({ ...workspace, updatedAt: new Date().toISOString() });
  setConsultingWorkspace(next);
  try { window.localStorage.setItem(storageKey(modelId), JSON.stringify(next)); } catch (_) {}
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('metricus:workspace-updated', { detail: { workspace: next } }));
    window.dispatchEvent(new CustomEvent('metricus:consulting-workspace-updated', { detail: { workspace: next } }));
  }
  saveWorkspaceToBackendBestEffort(modelId, next);
  return next;
};

const toEvidenceRows = (filterSummary: any[] = [], source: string, dataset: string) => {
  const rows = Array.isArray(filterSummary) ? filterSummary : [];
  if (rows.length > 0) return rows;
  return [
    { type: 'Source', description: source || 'Current view' },
    { type: 'Model', description: dataset || 'Current model' },
  ];
};

export const FindingModal = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [source, setSource] = React.useState('Current view');
  const [objectType, setObjectType] = React.useState('');
  const [objectLabel, setObjectLabel] = React.useState('');
  const [objectId, setObjectId] = React.useState('');
  const [objectRow, setObjectRow] = React.useState<any>(null);
  const [title, setTitle] = React.useState('');
  const [area, setArea] = React.useState('Performance');
  const [severity, setSeverity] = React.useState('Medium');
  const [confidence, setConfidence] = React.useState('Moderate');
  const [valuePotential, setValuePotential] = React.useState('Medium');
  const [status, setStatus] = React.useState('Needs Review');
  const [evidenceText, setEvidenceText] = React.useState('');
  const [recommendation, setRecommendation] = React.useState('');
  const [consultantNotes, setConsultantNotes] = React.useState('');
  const [tasks, setTasks] = React.useState(0);
  const [events, setEvents] = React.useState(0);
  const [filters, setFilters] = React.useState<any[]>([]);
  const [filterSummary, setFilterSummary] = React.useState<any[]>([]);

  useApiListener(
    Event.openFindingModal,
    ({ data }: FindingModalProps) => {
      const modalData: any = data || {};
      const sourceLabel = modalData.source || modalData.sourceArea || 'Current view';
      const dataset = getProcessModelTitle ? getProcessModelTitle() : 'Current model';
      const objectTypeLabel = String(modalData.objectType || '').trim();
      const objectName = String(modalData.objectLabel || '').trim();
      const objectIdentifier = String(modalData.objectId || '').trim();
      const rows = Array.isArray(modalData.filterSummary) ? modalData.filterSummary : [];
      const suggestedTitle = modalData.title || modalData.finding || `${objectName || sourceLabel} finding`;

      setSource(sourceLabel);
      setObjectType(objectTypeLabel);
      setObjectLabel(objectName);
      setObjectId(objectIdentifier);
      setObjectRow(modalData.objectRow || modalData.row || null);
      setTitle(suggestedTitle);
      setArea(CATEGORIES.includes(modalData.area || modalData.category) ? (modalData.area || modalData.category) : 'Performance');
      setSeverity(SEVERITIES.includes(modalData.severity) ? modalData.severity : 'Medium');
      setConfidence(CONFIDENCE.includes(modalData.confidence) ? modalData.confidence : 'Moderate');
      setValuePotential(VALUES.includes(modalData.valuePotential) ? modalData.valuePotential : 'Medium');
      setStatus(STATUSES.includes(modalData.status) ? modalData.status : 'Needs Review');
      setTasks(Number(modalData.tasks || 0));
      setEvents(Number(modalData.events || 0));
      setFilters(Array.isArray(modalData.filters) ? modalData.filters : []);
      setFilterSummary(rows);
      setEvidenceText(splitEvidence(modalData.evidence).join('\n') || rows.map(row => `${row.type || 'Evidence'}: ${row.description || ''}`).join('\n'));
      setRecommendation(modalData.recommendation || '');
      setConsultantNotes(modalData.consultantNotes || modalData.notes || '');
      setIsOpen(true);
    }
  );

  const saveFinding = () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) return;
    const workspace = loadWorkspace();
    const now = new Date().toISOString();
    const findingId = `finding_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const finding = {
      id: findingId,
      title: cleanTitle,
      finding: cleanTitle,
      area,
      severity,
      confidence,
      valuePotential,
      status,
      sourceArea: source,
      objectType,
      objectLabel,
      objectId,
      objectRow,
      evidence: splitEvidence(evidenceText),
      recommendation,
      consultantNotes,
      tasks,
      events,
      filters,
      filterSummary,
      createdAt: now,
      updatedAt: now,
    };

    saveWorkspace({
      ...workspace,
      findings: [finding, ...workspace.findings],
    });
    setIsOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      onOk={saveFinding}
      saveOK={title.trim().length > 0}
      title="Save Finding"
      width="600px"
      okLabel="Save Finding"
      cancelLabel="Cancel"
    >
      <Shell>
        <Guidance>
          <div className="icon">✓</div>
          <div className="copy">
            <strong>Capture one evidence-backed observation.</strong>
            Be specific: what was observed, why it matters, and what should be reviewed next.
          </div>
        </Guidance>

        <FocusBox>
          <div className="focus-title">Finding focus</div>
          <div className="focus-meta">
            <MetaPill><strong>Source</strong>{source || 'Current view'}</MetaPill>
            {objectType || objectLabel ? <MetaPill><strong>{objectType || 'Object'}</strong>{objectLabel || 'Selected item'}</MetaPill> : <MetaPill><strong>Focus</strong>Current view</MetaPill>}
            <MetaPill><strong>Model</strong>{getProcessModelTitle ? getProcessModelTitle() : 'Current model'}</MetaPill>
          </div>
        </FocusBox>

        <Grid>
          <Field span={12}>
            <span>Finding title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Excessive rework is delaying resolution" />
          </Field>

          <Field span={4}>
            <span>Category</span>
            <select value={area} onChange={(e) => setArea(e.target.value)}>{CATEGORIES.map(v => <option key={v}>{v}</option>)}</select>
          </Field>
          <Field span={4}>
            <span>Severity</span>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)}>{SEVERITIES.map(v => <option key={v}>{v}</option>)}</select>
          </Field>
          <Field span={4}>
            <span>Value potential</span>
            <select value={valuePotential} onChange={(e) => setValuePotential(e.target.value)}>{VALUES.map(v => <option key={v}>{v}</option>)}</select>
          </Field>
          <Field span={6}>
            <span>Confidence</span>
            <select value={confidence} onChange={(e) => setConfidence(e.target.value)}>{CONFIDENCE.map(v => <option key={v}>{v}</option>)}</select>
          </Field>
          <Field span={6}>
            <span>Review status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map(v => <option key={v}>{v}</option>)}</select>
          </Field>

          <Field span={12}>
            <span>Evidence observed</span>
            <textarea value={evidenceText} onChange={(e) => setEvidenceText(e.target.value)} placeholder="Add the specific evidence observed for this metric, variant, step, discovery rule, chart item, or selected object." />
          </Field>

          <Field span={12}>
            <span>Why it matters / recommendation</span>
            <textarea value={recommendation} onChange={(e) => setRecommendation(e.target.value)} placeholder="What is the business/process impact, and what should be considered?" />
          </Field>

          <Field span={12}>
            <span>Consultant notes</span>
            <textarea value={consultantNotes} onChange={(e) => setConsultantNotes(e.target.value)} placeholder="Assumptions, validation notes, client context, or follow-up questions" />
          </Field>
        </Grid>

        <NextStep>
          <strong>Next:</strong> save it to Actions, validate it with the client/team, then convert it to action only when there is an agreed change.
        </NextStep>
      </Shell>
    </Modal>
  );
};
